import { writable, derived } from 'svelte/store';
import { targetOf } from './runContext.js';

// AutoTrader (live/paper) dashboard state (U6). Write primitives + derived composites, mirroring the backtest
// block in store.js: nothing here is a synchronized duplicate — the composites derive from the primitives.
//
// The live NAV/alert SSE stores (`autotradeNav`, `autotradeAlerts`) live in sseStore.ts (they receive the raw
// events); SSE is a HINT — authoritative values come from refetching getStrategy + getStrategyPerformance on
// NAV ticks (debounced) and on connect. These stores hold the authoritative, refetched state.

/** The caller's deployed strategies (snapshots), newest-first — the running-strategies list source. */
export const autotradeStrategies = writable([]);

/** The strategy id currently selected in the dashboard (drives the performance + status panels). */
export const selectedStrategyId = writable(null);

/** The authoritative performance result for the selected strategy (from getStrategyPerformance). */
export const strategyPerformance = writable(null);

/** True while the selected strategy's snapshot/performance refetch is in flight. */
export const strategyLoading = writable(false);

/** A dashboard-level error message (cleared on a successful refetch). */
export const strategyError = writable(null);

/** The selected strategy's snapshot (status, mode, latest NAV mark, bar summary), derived from the list. */
export const selectedStrategy = derived(
  [autotradeStrategies, selectedStrategyId],
  ([$strategies, $id]) => ($id ? $strategies.find((s) => s.strategyId === $id) ?? null : null)
);

/**
 * The selected strategy's lifecycle status (Deployed/Starting/Running/Pausing/Paused/Stopping/Stopped/
 * Faulted/Undeploying/Undeployed), or null when nothing is selected. The view binds this for the status chip
 * and the lifecycle-control enablement. ('Undeployed' is terminal/archived — the backend filters it out of the
 * list, so a selected strategy never reads as 'Undeployed'; 'Undeploying' shows as "Removing…".)
 */
export const strategyStatus = derived(selectedStrategy, ($s) => $s?.status ?? null);

/**
 * The selected strategy's persisted RUN TARGET `{ venue, mode }` (ADR-0040; `exchange` + `mode` on the
 * snapshot) — SAFETY-CRITICAL: the theme/banner derives from THIS (the freshly-refetched persisted target),
 * never from which context or view is open. A paper-themed screen must never render a real-money strategy.
 * Null when nothing is selected OR the snapshot's target is unrecognised (fail-closed: rendered as UNKNOWN,
 * never as live and never silently as paper).
 */
export const selectedStrategyTarget = derived(selectedStrategy, ($s) => targetOf($s));

// ---------------------------------------------------------------------------------------------------------
// Workspace view-state + tabs (U11/KTD10). The dashboard toggles between the New Strategy deploy form and the
// tabbed deployed-strategy workspace; `view` is derived from the async-loaded strategy count (default 'new'
// until loadStrategies resolves — never flash an empty workspace, per the feasibility-review ordering caveat).
// ---------------------------------------------------------------------------------------------------------

/** 'new' shows the deploy form; 'deployed' shows the StrategyWorkspace. Default 'new' until the count loads. */
export const view = writable('new');

/** The active workspace tab (U11): overview | candles | trades | parameters | allocation. */
export const activeTab = writable('overview');

/** Top-level surface switch (U15): 'workspace' (per-strategy) | 'portfolio' (cross-strategy aggregate). */
export const surface = writable('workspace');

// ---------------------------------------------------------------------------------------------------------
// Runtime adjustments (U14/KTD4). Each accept returns 202 + adjustmentId; we hold it Pending and flip it to
// Applied/Failed on the SSE status event (poll fallback in the tab). Keyed by adjustment id, newest-first list
// derived for the pending-strip. The map carries the locally-known kind/org/amount so the strip can render
// before the first GET /adjustments refetch.
// ---------------------------------------------------------------------------------------------------------

/** Pending/recent adjustments keyed by id: { id, requestId, orgId, kind, amount, status, failureReason, at }. */
export const adjustments = writable(new Map());

/** The selected strategy's adjustments, newest-first, scoped to the selection (the pending-strip source). */
export const selectedAdjustments = derived(
  [adjustments, selectedStrategyId],
  ([$adj, $id]) => {
    if (!$id) return [];
    return [...$adj.values()]
      .filter((a) => String(a.requestId) === String($id))
      .sort((a, b) => (b.at ?? 0) - (a.at ?? 0));
  }
);

/**
 * Record a freshly-accepted adjustment as Pending (optimistic insert before the first status event/refetch).
 * `kind` is the AdjustmentKind string; `amount`/`orgId` are the locally-known values for the strip label.
 */
export function recordPendingAdjustment(id, { requestId, orgId, kind, amount } = {}) {
  if (!id) return;
  adjustments.update((m) => {
    const next = new Map(m);
    next.set(String(id), {
      id: String(id), requestId, orgId, kind, amount,
      status: 'Pending', failureReason: null, at: Date.now(),
    });
    return next;
  });
}

/**
 * Reconcile an adjustment-status SSE event (U10): flip the matching row to Applied/Failed (or insert it if the
 * event arrives before the optimistic record — e.g. a teammate's action). Log-safe payload: id/kind/status/reason.
 */
export function applyAdjustmentStatus(payload) {
  const id = payload?.adjustmentId ?? payload?.AdjustmentId ?? payload?.id ?? payload?.Id;
  if (!id) return;
  adjustments.update((m) => {
    const next = new Map(m);
    const prev = next.get(String(id)) ?? {};
    next.set(String(id), {
      ...prev,
      id: String(id),
      requestId: payload.requestId ?? payload.RequestId ?? prev.requestId,
      orgId: payload.orgId ?? payload.OrgId ?? prev.orgId,
      kind: payload.kind ?? payload.Kind ?? prev.kind,
      status: payload.status ?? payload.Status ?? prev.status ?? 'Pending',
      failureReason: payload.failureReason ?? payload.FailureReason ?? prev.failureReason ?? null,
      at: prev.at ?? Date.now(),
    });
    return next;
  });
}

/** Replace the adjustments map from a GET /adjustments refetch (the poll-fallback / authoritative reconcile). */
export function setAdjustmentsFromServer(rows) {
  if (!Array.isArray(rows)) return;
  adjustments.update((m) => {
    const next = new Map(m);
    for (const r of rows) {
      const id = String(r.id ?? r.Id);
      const prev = next.get(id) ?? {};
      next.set(id, {
        id,
        requestId: r.requestId ?? r.RequestId ?? prev.requestId,
        orgId: r.orgId ?? r.OrgId ?? prev.orgId,
        kind: r.kind ?? r.Kind ?? prev.kind,
        amount: r.amount ?? r.Amount ?? prev.amount ?? null,
        status: r.status ?? r.Status ?? prev.status,
        failureReason: r.failureReason ?? r.FailureReason ?? prev.failureReason ?? null,
        at: new Date(r.createdAt ?? r.CreatedAt ?? Date.now()).getTime(),
      });
    }
    return next;
  });
}

// ---------------------------------------------------------------------------------------------------------
// Transparency live hint (U12b/KTD11). A monotonically-increasing counter bumped on each NAV-tick SSE hint
// (via the host's `onNavTick` callback). The Candles & Signals tab watches it and re-pulls its signal series
// (U8c) — the KTD7 hint-then-refetch pattern, so the chart advances live without a dedicated signal-bar push.
// ---------------------------------------------------------------------------------------------------------

/** Bumped on each NAV tick; the transparency tab reacts to it to refetch its signal series live. */
export const navTickSeq = writable(0);

/** Bump the NAV-tick hint (called from the SSE host on an `autotrade-nav` event). */
export function bumpNavTick() {
  navTickSeq.update((n) => n + 1);
}

// ---------------------------------------------------------------------------------------------------------
// Decision-lineage live hint (TASK-20260728-monthly-export-decision-sse). A monotonic counter bumped on each
// `autotrade-decision` SSE watermark. The Decisions/Execution/Book tabs watch it and refetch their reads on a
// REAL decision — a precise hint over the coarser NAV cadence. The NAV-tick hint stays as belt-and-suspenders.
// ---------------------------------------------------------------------------------------------------------

/** Bumped on each decision-lineage watermark; the observability tabs refetch on it. */
export const decisionTickSeq = writable(0);

/** Bump the decision hint (called from the SSE host on an `autotrade-decision` event). */
export function bumpDecisionTick() {
  decisionTickSeq.update((n) => n + 1);
}

// ---------------------------------------------------------------------------------------------------------
// Portfolio surface (U15/U10b). Server-aggregated NAV/metrics/blotter + join-discovery for the current user.
// ---------------------------------------------------------------------------------------------------------

export const portfolioPerformance = writable(null);
export const portfolioBlotter = writable([]);
export const portfolioDiscoverable = writable([]);
export const portfolioLoading = writable(false);
export const portfolioError = writable(null);

/**
 * Reset the dashboard's selection + transient state. Called on an org switch (freshness guard — a NAV event or
 * stale performance from the previous org must never populate the newly-selected org's dashboard) and may be
 * called on sign-out. Does NOT clear the SSE alert feed here; that is owned by sseStore. Also clears the
 * per-strategy transparency series + adjustment map so org B never inherits org A's transient surfaces.
 */
export function resetAutotradeSelection() {
  selectedStrategyId.set(null);
  strategyPerformance.set(null);
  strategyError.set(null);
  strategyLoading.set(false);
  activeTab.set('overview');
  adjustments.set(new Map());
}

/**
 * Extract the existing strategy id from a deploy 409-conflict error, or null when the error isn't a conflict.
 * The deploy endpoint returns 409 with a body `{ message, strategyId }` for an idempotent re-deploy (the same
 * strategy+symbols is already active); fetchWithErrorHandling throws it as
 * `HTTP error! status: 409, details: {...json...}`. This parses that body so the UI can SELECT the existing
 * strategy instead of showing a raw error.
 */
export function conflictStrategyId(err) {
  const msg = String(err?.message ?? err);
  if (!msg.includes('status: 409')) return null;
  const idx = msg.indexOf('details: ');
  if (idx < 0) return null;
  try {
    const body = JSON.parse(msg.slice(idx + 'details: '.length));
    return body?.strategyId ?? null;
  } catch {
    return null;
  }
}
